import { AlertTriangle, Info, Minus } from 'lucide-react';

import { pluralize } from '@/react/common/string-utils';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';

import { Button } from '@@/buttons';
import { Modal, OnSubmit } from '@@/modals/Modal';
import { TextTip } from '@@/Tip/TextTip';
import { Loading } from '@@/Widget/Loading';

import { PolicyType } from '../../types';
import {
  DetachedGroupInfo,
  usePolicyConflicts,
} from '../../queries/usePolicyConflicts';

import { GroupItem } from './GroupItem';

interface PolicyPreviewModalProps {
  /** Name of the policy being created/edited */
  policyName: string;
  /** Type of the policy */
  policyType: PolicyType;
  /** ID of the policy being edited (null for new policies) */
  policyId: number | null;
  /** IDs of environment groups selected for this policy */
  environmentGroupIds: Array<number>;
  /** Groups being detached (computed by caller) */
  detachedGroups: Array<DetachedGroupInfo>;
  /** Callback when user confirms or cancels */
  onSubmit: OnSubmit<boolean>;
}

export function PolicyPreviewModal({
  policyName,
  policyType,
  policyId,
  environmentGroupIds,
  detachedGroups,
  onSubmit,
}: PolicyPreviewModalProps) {
  // Only fetch conflicts if we have environment groups to check
  const hasGroupsToCheck = environmentGroupIds.length > 0;

  const conflictsQuery = usePolicyConflicts(
    {
      policyId,
      type: policyType,
      environmentGroups: environmentGroupIds,
    },
    { enabled: hasGroupsToCheck }
  );

  // Use isInitialLoading to prevent showing loading spinner on background refetches
  // When enabled is false, isInitialLoading is false (unlike isLoading which stays true)
  const isLoading = hasGroupsToCheck && conflictsQuery.isInitialLoading;
  const { isError } = conflictsQuery;
  // Note: Error notifications are handled by withGlobalError in usePolicyConflicts

  const conflicts = conflictsQuery.data?.conflicts ?? [];
  const newGroups = conflictsQuery.data?.newGroups ?? [];
  const totalEnvironments = conflictsQuery.data?.totalEnvironments ?? 0;

  const totalGroups = conflicts.length + newGroups.length;
  const hasConflicts = conflicts.length > 0;
  const hasNewGroups = newGroups.length > 0;
  const hasDetachedGroups = detachedGroups.length > 0;

  return (
    <Modal
      onDismiss={() => onSubmit(false)}
      aria-label="Policy Distribution Preview"
      size="lg"
    >
      <Modal.Header title="Policy Distribution Preview" />

      <Modal.Body>
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-8">
            <Loading />
            <span className="text-muted mt-3">Loading policy preview...</span>
          </div>
        )}

        {isError && (
          <div className="space-y-4 py-8 text-center">
            <TextTip color="red" icon={AlertTriangle} inline={false}>
              Unable to load policy preview
            </TextTip>
            <p className="text-muted">
              We could not verify potential policy conflicts. You can still
              proceed with the action, but we recommend checking for conflicts
              manually.
            </p>
          </div>
        )}

        {!isLoading && !isError && (
          <div className="space-y-4">
            {/* Conflicts Section */}
            {hasConflicts && (
              <div className="space-y-2">
                <TextTip color="orange" icon={AlertTriangle} inline={false}>
                  <span className="font-medium">
                    The following environment groups will have their policy
                    replaced:
                  </span>
                </TextTip>
                <ul className="ml-6 list-none space-y-2 p-0">
                  {conflicts.map((conflict) => (
                    <GroupItem
                      key={conflict.environmentGroupId}
                      variant="conflict"
                      groupId={conflict.environmentGroupId}
                      groupName={conflict.environmentGroupName}
                      environmentCount={conflict.environmentCount}
                      supportedEnvironments={conflict.supportedEnvironments}
                      unsupportedEnvironments={conflict.unsupportedEnvironments}
                      policyType={policyType}
                      subtitle={`${conflict.existingPolicyName} → ${policyName}`}
                    />
                  ))}
                </ul>
              </div>
            )}

            {/* New Groups Section */}
            {hasNewGroups && (
              <div className="space-y-2">
                <TextTip color="blue" icon={Info} inline={false}>
                  <span className="font-medium">
                    The following groups will receive this policy:
                  </span>
                </TextTip>
                <ul className="ml-6 list-none space-y-2 p-0">
                  {newGroups.map((group) => (
                    <GroupItem
                      key={group.environmentGroupId}
                      variant="new"
                      groupId={group.environmentGroupId}
                      groupName={group.environmentGroupName}
                      environmentCount={group.environmentCount}
                      supportedEnvironments={group.supportedEnvironments}
                      unsupportedEnvironments={group.unsupportedEnvironments}
                      policyType={policyType}
                      subtitle={`The policy "${policyName}" will override any previous environment-level settings`}
                    />
                  ))}
                </ul>
              </div>
            )}

            {/* Detached Groups Section */}
            {hasDetachedGroups && (
              <div className="space-y-2">
                <TextTip color="blue" icon={Minus} inline={false}>
                  <span className="font-medium">
                    The following groups will be detached from this policy:
                  </span>
                </TextTip>
                <ul className="ml-6 list-none space-y-2 p-0">
                  {detachedGroups.map((group) => (
                    <GroupItem
                      key={group.environmentGroupId}
                      variant="detached"
                      groupId={group.environmentGroupId}
                      groupName={group.environmentGroupName}
                      environmentCount={group.environmentCount}
                      subtitle="This group will no longer receive updates from this policy"
                    />
                  ))}
                </ul>
              </div>
            )}

            {/* Summary */}
            <div className="text-muted border-t border-gray-5 pt-3 text-sm th-highcontrast:border-white th-dark:border-gray-7">
              Total: {totalGroups} {pluralize(totalGroups, 'environment group')}{' '}
              • {totalEnvironments}{' '}
              {pluralize(totalEnvironments, 'environment')}
              {hasDetachedGroups && (
                <span>
                  {' '}
                  • {detachedGroups.length}{' '}
                  {pluralize(detachedGroups.length, 'group')} detached
                </span>
              )}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          onClick={() => onSubmit(false)}
          color="default"
          data-cy="cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit(true)}
          color="primary"
          disabled={isLoading}
          data-cy="confirm-button"
        >
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Computes groups that are being detached (removed from the policy).
 * These are groups that exist in original but not in the new selection.
 */
export function computeDetachedGroups(
  originalIds: Array<number>,
  newIds: Array<number>,
  groups: Array<EnvironmentGroup>
): Array<DetachedGroupInfo> {
  const newIdsSet = new Set(newIds);
  const detachedIds = originalIds.filter((id) => !newIdsSet.has(id));

  return detachedIds
    .map((id) => {
      const group = groups.find((g) => g.Id === id);
      if (!group) return null;
      return {
        environmentGroupId: group.Id,
        environmentGroupName: group.Name,
        environmentCount: group.Size || 0,
      };
    })
    .filter((g): g is DetachedGroupInfo => g !== null);
}
